import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  TrendingUp,
  Shield,
  ArrowRight,
  CheckCircle2,
  Zap,
  Lock,
  Unlock,
  Sparkles,
  Wallet,
  Calendar,
  RefreshCw,
} from 'lucide-react';

const PACKAGES = [
  {
    id: 'flexible',
    name: 'Flexible',
    yield: 7,
    lock: 0,
    color: 'amber',
    icon: Unlock,
    desc: 'Liquidez total. Retira tu capital cuando quieras.',
  },
  {
    id: 'mid',
    name: 'Mid-Lock',
    yield: 8,
    lock: 6,
    color: 'blue',
    icon: Lock,
    desc: 'Bloqueo de 6 meses. Equilibrio entre rendimiento y liquidez.',
  },
  {
    id: 'long',
    name: 'Long-Lock',
    yield: 9,
    lock: 12,
    color: 'emerald',
    icon: Sparkles,
    desc: 'Bloqueo de 12 meses. Maxima rentabilidad de la plataforma.',
  },
];

const COLORS = {
  amber: 'from-amber-500/20 to-amber-600/5 border-amber-500/30 text-amber-400',
  blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/30 text-blue-400',
  emerald: 'from-emerald-500/20 to-emerald-600/5 border-emerald-500/30 text-emerald-400',
};

export const Landing = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <Zap className="w-6 h-6 text-black" />
            </div>
            <span className="font-bold text-xl tracking-tight text-amber-50">Invictus</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-amber-100/70 hover:text-amber-100 hover:bg-amber-500/10">
                Iniciar Sesion
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/25">
                Empezar
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-500/10 rounded-full blur-[150px]" />

        <div className="max-w-7xl mx-auto relative">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-8">
              <Wallet className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium text-amber-400">Inversion en USDC/USDT - Red Polygon</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-[1.05] tracking-tight">
              Hasta{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500">
                9% mensual
              </span>
              <br />
              en stablecoins
            </h1>

            <p className="text-xl text-white/60 mb-10 max-w-2xl mx-auto leading-relaxed">
              Conecta tu wallet, deposita USDC o USDT y obten rendimientos mensuales auto-acreditados.
              Sin custodia centralizada. Sin intermediarios.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Link to="/register">
                <Button
                  data-testid="hero-cta-register"
                  size="lg"
                  className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold hover:from-amber-400 hover:to-amber-500 px-8 py-6 text-lg rounded-xl shadow-lg shadow-amber-500/30"
                >
                  Empezar a Invertir
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-amber-500/30 text-amber-100 hover:bg-amber-500/10 px-8 py-6 text-lg rounded-xl"
                >
                  Acceder a mi cuenta
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-3 text-xs text-white/40">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Custodia Web3
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> USDC y USDT
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Min. $10,000
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Yield mensual auto
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="py-20 px-6 border-t border-amber-500/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-3">3 paquetes. Tu eliges la rentabilidad</h2>
            <p className="text-white/50 max-w-xl mx-auto">
              Mas plazo, mas rentabilidad. Reclama tu yield mensual o capitalizalo automaticamente.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {PACKAGES.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.id}
                  data-testid={`landing-pkg-${p.id}`}
                  className={`relative p-8 rounded-2xl bg-gradient-to-br ${COLORS[p.color]} border group hover:scale-[1.02] transition-all duration-300`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className={`w-11 h-11 rounded-xl bg-black/30 flex items-center justify-center`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-mono px-2 py-1 rounded bg-black/30 border border-current/20">
                      {p.lock === 0 ? 'Sin bloqueo' : `${p.lock} meses`}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold mb-2">{p.name}</h3>
                  <p className="text-sm text-white/50 mb-6">{p.desc}</p>

                  <div className="text-6xl font-bold mb-1">{p.yield}%</div>
                  <div className="text-xs uppercase tracking-wider text-white/40">Mensual</div>

                  <div className="mt-6 pt-6 border-t border-current/20 text-xs text-white/50">
                    <span className="font-mono">{p.yield * 12}% anual</span> sobre tu capital
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Link to="/register">
              <Button className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold px-8 py-5 text-base rounded-xl shadow-lg">
                Ver Paquetes Detallados
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent via-amber-500/5 to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-3">Como funciona</h2>
            <p className="text-white/50">Tres pasos para empezar a generar rendimientos.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                num: '01',
                icon: Wallet,
                title: 'Conecta tu Wallet',
                desc: 'MetaMask o cualquier wallet compatible con Polygon.',
              },
              {
                num: '02',
                icon: TrendingUp,
                title: 'Elige tu Paquete',
                desc: 'Flexible 7%, Mid-Lock 8% o Long-Lock 9% mensual.',
              },
              {
                num: '03',
                icon: RefreshCw,
                title: 'Reclama o Capitaliza',
                desc: 'El dia 1 de cada mes recibes tu yield. Tu eliges.',
              },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className="relative p-7 rounded-2xl bg-zinc-900/60 border border-amber-500/15 hover:border-amber-500/30 transition-all"
                >
                  <div className="text-5xl font-bold text-amber-500/15 absolute top-3 right-5">
                    {s.num}
                  </div>
                  <Icon className="w-7 h-7 text-amber-400 mb-3" />
                  <h3 className="text-lg font-semibold mb-2">{s.title}</h3>
                  <p className="text-sm text-white/50">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-20 px-6 border-t border-amber-500/10">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-4">Seguridad y transparencia Web3</h2>
            <p className="text-white/50 mb-6">
              Tu capital se transfiere directamente a una direccion publica de tesoreria via smart contract en Polygon.
              Sin custodios centralizados que puedan congelar tus fondos.
            </p>
            <ul className="space-y-3">
              {[
                'Depositos via MetaMask en stablecoins (USDC/USDT)',
                'Direccion de tesoreria publica y auditable on-chain',
                'Yield calculado matematicamente y acreditado el dia 1 de cada mes',
                'Reclama tu rendimiento en cualquier momento o capitalizalo',
                'Comision de referidos: 2% del capital del referido',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-white/70">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-8 rounded-3xl bg-gradient-to-br from-amber-500/10 via-amber-600/5 to-transparent border border-amber-500/20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-72 h-72 bg-amber-500/10 rounded-full blur-[80px]" />
            <Shield className="w-9 h-9 text-amber-400 mb-3 relative" />
            <h3 className="text-2xl font-bold mb-3 relative">Sin intermediarios</h3>
            <p className="text-white/60 mb-6 relative">
              Cada deposito es una transaccion blockchain verificable. Cero confianza requerida en la plataforma.
            </p>
            <div className="grid grid-cols-2 gap-4 relative">
              <div className="p-4 rounded-xl bg-black/30 border border-amber-500/15">
                <Calendar className="w-5 h-5 text-amber-400 mb-2" />
                <div className="text-sm text-white/40">Yield acreditado</div>
                <div className="font-mono font-bold text-white">Dia 1/mes</div>
              </div>
              <div className="p-4 rounded-xl bg-black/30 border border-amber-500/15">
                <Wallet className="w-5 h-5 text-amber-400 mb-2" />
                <div className="text-sm text-white/40">Red</div>
                <div className="font-mono font-bold text-white">Polygon</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-5 text-amber-50">
            Tu capital trabajando 24/7
          </h2>
          <p className="text-white/50 mb-10 max-w-lg mx-auto">
            Crea tu cuenta gratis, conecta tu wallet y empieza a generar rendimientos en stablecoins.
          </p>
          <Link to="/register">
            <Button
              data-testid="cta-register-final"
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold hover:from-amber-400 hover:to-amber-500 px-10 py-6 text-lg rounded-xl shadow-lg shadow-amber-500/30"
            >
              Crear Cuenta Gratis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-amber-500/20">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" />
            </div>
            <span className="font-semibold text-amber-50">Invictus</span>
          </div>
          <p className="text-sm text-white/40">
            Inversion Web3 en stablecoins - Red Polygon
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
